import React, { useRef, useCallback } from 'react';
import { GoogleMap, Marker, MarkerClusterer, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import MapPinAlert from 'assets/icons/map-pin-alert.png';
import MapPinLocked from 'assets/icons/map-pin-locked.png';
import MapPinUnlocked from 'assets/icons/map-pin-unlocked.png';
import { useAppSettings } from 'context/appsettingsContext';
import './GlobalMapview.scss';
import VehicleCard from 'components/VehicleCard/VehicleCard';

interface GlobalMapviewProps {
  vehicles: {
    lat: number | null;
    lng: number | null;
    isLocked: boolean;
    tamperAlert: boolean;
    vehicleInfo: {
      make: string;
      registration: string;
      nickname: string;
      deviceSerialNumber: string;
    };
    isTrackerOnly?: boolean;
  }[];
  onMarkerClick: (vehicle: any) => void;
}

const GlobalMapview: React.FC<GlobalMapviewProps> = ({ vehicles, onMarkerClick }) => {
  const settings = useAppSettings();
  const [selectedVehicle, setSelectedVehicle] = React.useState<any>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: settings.googleMapsApiKey,
  });

  const mapPinStatus = {
    alert: MapPinAlert,
    locked: MapPinLocked,
    unlocked: MapPinUnlocked,
  };

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    
    // Only set initial bounds if we haven't set them before
    if (vehicles.length > 0 && !mapRef.current.getCenter()) {
      const bounds = new google.maps.LatLngBounds();
      vehicles.forEach((vehicle) => {
        if (vehicle.lat && vehicle.lng) {
          bounds.extend({ lat: vehicle.lat, lng: vehicle.lng });
        }
      });
      map.fitBounds(bounds);
    }
  }, [vehicles]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  return (
    <div className='global-map-view' aria-label='map'>
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={() => setSelectedVehicle(null)}
        >
          {vehicles.length > 0 && (
            <MarkerClusterer
              options={{
                gridSize: 60,
                maxZoom: 14,
                averageCenter: true,
              }}
            >
              {(clusterer) => (
                <>
                  {vehicles.map((vehicle, index) => (
                    <Marker
                      key={index}
                      position={{ lat: vehicle.lat!, lng: vehicle.lng! }}
                      clusterer={clusterer}
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        onMarkerClick(vehicle);
                      }}
                      icon={{
                        url: vehicle.tamperAlert === true
                          ? mapPinStatus['alert']
                          : mapPinStatus[vehicle.isLocked ? 'locked' : 'unlocked'],
                      }}
                    />
                  ))}
                </>
              )}
            </MarkerClusterer>
          )}

          {selectedVehicle && (
            console.log(selectedVehicle),
            <InfoWindow
              position={{ lat: selectedVehicle.lat, lng: selectedVehicle.lng }}
              options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
              onCloseClick={() => setSelectedVehicle(null)}
            >
              <div className='vehicle' key={selectedVehicle.id}>
                <VehicleCard
                  make={selectedVehicle.vehicleInfo.make}
                  registration={selectedVehicle.vehicleInfo.registration}
                  deviceActive={true}
                  gps={{ lat: selectedVehicle.lat, lng: selectedVehicle.lng }}
                  gpsDateTime={selectedVehicle.vehicleInfo.gpsDateTime}
                  lastDateTime={selectedVehicle.vehicleInfo.lastDateTime}
                  isLocked={selectedVehicle.isLocked}
                  nickname={selectedVehicle.vehicleInfo.nickname}
                  tamperActive={selectedVehicle.tamperAlert}
                  drivers={[]}
                  vehicleId={selectedVehicle.vehicleInfo.vehicleId}
                  deviceId={selectedVehicle.vehicleInfo.deviceId}
                  deviceSerialNumber={selectedVehicle.vehicleInfo.deviceId}
                  isTrackerOnly={selectedVehicle.vehicleInfo.isTrackerOnly}
                />
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      )}
    </div>
  );
};

export default GlobalMapview;